/**
 * SpeedmasterCore.Header.js — Shared header/search registrations
 *
 * Extracted from Shopping.js so MyAccount and Checkout get the same
 * enhanced header: mega menu, search quick links, logo badge, translations.
 *
 * Called by Shopping.js, MyAccount.js, and Checkout.js mountToApp().
 */
define('SpeedmasterCore.Header', [
    'SC.Configuration'
], function(Configuration) {
    'use strict';

    /* ------------------------------------------------------------------ */
    /*  Category helpers                                                    */
    /* ------------------------------------------------------------------ */

    /**
     * Normalize category text for matching — handles HTML entities,
     * extra whitespace, and case differences.
     */
    function normalizeCatText(str) {
        return (str + '')
            .replace(/^\s+|\s+$/g, '')
            .replace(/&amp;/g, '&')
            .replace(/&#39;/g, "'")
            .replace(/&quot;/g, '"')
            .toLowerCase();
    }

    function shallowCopy(src) {
        var dest = {};
        for (var k in src) {
            if (src.hasOwnProperty(k)) { dest[k] = src[k]; }
        }
        return dest;
    }

    /* ------------------------------------------------------------------ */
    /*  Public API                                                          */
    /* ------------------------------------------------------------------ */

    return {
        /**
         * Register all shared header context definitions.
         * @param {LayoutComponent} layout
         */
        register: function register(layout) {

            /* ========================================================== */
            /*  TRANSLATION OVERRIDES                                      */
            /* ========================================================== */

            if (typeof SC !== 'undefined') {
                SC.Translations = SC.Translations || {};
                SC.Translations['Out of Stock'] = 'Sold Out';
            }

            /* ========================================================== */
            /*  LIGHT LOGO — Header.Logo.View                            */
            /* ========================================================== */

            layout.addToViewContextDefinition(
                'Header.Logo.View', 'lightLogoUrl', 'string',
                function() {
                    return Configuration.get('speedmaster.LightLogoUrl') || '';
                }
            );

            /* ========================================================== */
            /*  STORE BADGE — Header.Logo.View                            */
            /*  Checks seasonal badges list first (date-range match),     */
            /*  falls back to default StoreBadgeIconUrl / StoreBadgeLabel. */
            /* ========================================================== */

            var seasonalBadges = Configuration.get('speedmaster.SeasonalBadges') || [];
            var activeSeason = null;

            if (seasonalBadges.length) {
                var now = new Date();
                var todayStr = now.getFullYear()
                    + '-' + (now.getMonth() < 9 ? '0' : '') + (now.getMonth() + 1)
                    + '-' + (now.getDate() < 10 ? '0' : '') + now.getDate();

                for (var sb = 0; sb < seasonalBadges.length; sb++) {
                    var badge = seasonalBadges[sb];
                    if (badge.start && badge.end && todayStr >= badge.start && todayStr <= badge.end) {
                        activeSeason = badge;
                        break; // first match wins
                    }
                }
            }

            var defaultBadgeIcon = Configuration.get('speedmaster.StoreBadgeIconUrl') || '';
            var defaultBadgeLabel = Configuration.get('speedmaster.StoreBadgeLabel') || '';

            layout.addToViewContextDefinition(
                'Header.Logo.View', 'storeBadgeIconUrl', 'string',
                function() {
                    return (activeSeason && activeSeason.icon) ? activeSeason.icon : defaultBadgeIcon;
                }
            );

            layout.addToViewContextDefinition(
                'Header.Logo.View', 'storeBadgeLabel', 'string',
                function() {
                    return (activeSeason && activeSeason.label) ? activeSeason.label : defaultBadgeLabel;
                }
            );

            /* ========================================================== */
            /*  SEARCH QUICK LINKS — SiteSearch.View                       */
            /* ========================================================== */

            var quickLinks = Configuration.get('speedmaster.SearchQuickLinks') || [];

            layout.addToViewContextDefinition(
                'SiteSearch.View', 'searchQuickLinks', 'object',
                function() {
                    return quickLinks;
                }
            );

            layout.addToViewContextDefinition(
                'SiteSearch.View', 'showQuickLinks', 'boolean',
                function() {
                    return quickLinks.length > 0;
                }
            );

            /* ========================================================== */
            /*  CATEGORY ENHANCEMENTS — Header.Menu.View                  */
            /* ========================================================== */

            var shortNamesArr = Configuration.get('speedmaster.CategoryShortNames') || [];
            var shortNamesMap = {};
            for (var sn = 0; sn < shortNamesArr.length; sn++) {
                var entry = shortNamesArr[sn];
                if (entry.fullName) {
                    shortNamesMap[normalizeCatText(entry.fullName)] = {
                        shortName: entry.shortName || '',
                        iconUrl: entry.iconUrl || ''
                    };
                }
            }

            var subcatImagesArr = Configuration.get('speedmaster.SubcategoryImages') || [];
            var subcatImagesMap = {};
            for (var si2 = 0; si2 < subcatImagesArr.length; si2++) {
                var img = subcatImagesArr[si2];
                if (img.name) {
                    subcatImagesMap[normalizeCatText(img.name)] = {
                        imageUrl: img.imageUrl || ''
                    };
                }
            }

            function enhanceSubcategories(subs) {
                if (!subs) { return subs; }
                var result = [];
                for (var si = 0; si < subs.length; si++) {
                    var sub = shallowCopy(subs[si]);
                    var subKey = sub.text ? normalizeCatText(sub.text) : '';
                    if (subKey && subcatImagesMap[subKey]) {
                        sub.imageUrl = subcatImagesMap[subKey].imageUrl;
                    }
                    result.push(sub);
                }
                return result;
            }

            layout.addToViewContextDefinition(
                'Header.Menu.View', 'enhancedCategories', 'object',
                function(context) {
                    if (!context.categories) { return []; }
                    var result = [];
                    for (var ci = 0; ci < context.categories.length; ci++) {
                        var cat = context.categories[ci];
                        var enhanced = shallowCopy(cat);
                        var catKey = cat.text ? normalizeCatText(cat.text) : '';
                        if (catKey && shortNamesMap[catKey]) {
                            enhanced.shortName = shortNamesMap[catKey].shortName;
                            enhanced.iconUrl = shortNamesMap[catKey].iconUrl;
                        }
                        if (enhanced.categories) {
                            enhanced.categories = enhanceSubcategories(enhanced.categories);
                        }
                        result.push(enhanced);
                    }
                    return result;
                }
            );

            /* ========================================================== */
            /*  EXPLORE SECTIONS — Header.View                           */
            /*  Flat config (section, text, desc, href) grouped into     */
            /*  sections array for the 3-column mega panel template.     */
            /* ========================================================== */

            var exploreLinks = Configuration.get('speedmaster.ExploreLinks') || [];
            var exploreSections = [];
            var sectionMap = {};

            for (var i = 0; i < exploreLinks.length; i++) {
                var link = exploreLinks[i];
                var sectionName = link.section || 'Links';

                if (!sectionMap[sectionName]) {
                    sectionMap[sectionName] = { heading: sectionName, links: [] };
                    exploreSections.push(sectionMap[sectionName]);
                }

                sectionMap[sectionName].links.push({
                    text: link.text,
                    desc: link.desc,
                    href: link.href
                });
            }

            layout.addToViewContextDefinition(
                'Header.View', 'exploreSections', 'object',
                function() {
                    return exploreSections;
                }
            );

            layout.addToViewContextDefinition(
                'Header.View', 'showExplore', 'boolean',
                function() {
                    return exploreSections.length > 0;
                }
            );

            /* ========================================================== */
            /*  MINICART THUMBNAIL — Header.MiniCartItemCell.View         */
            /* ========================================================== */

            layout.addToViewContextDefinition(
                'Header.MiniCartItemCell.View', 'thumbnail', 'object',
                function(context) {
                    return {
                        url: context.line && context.line.item
                            ? (context.line.item.custitemitemimageurl1 || (context.thumbnail && context.thumbnail.url) || '')
                            : (context.thumbnail && context.thumbnail.url) || '',
                        altimagetext: context.thumbnail ? context.thumbnail.altimagetext : ''
                    };
                }
            );
        }
    };
});
